import {Box, Stack, Typography} from '@mui/material';
import React, {useRef, useState} from 'react';
import ArrButton from '../../../../common/arr-button/ArrButton';
import {t} from 'i18next';
import {Criteria, CriteriaType} from '../../../types/Criteria';
import {Query} from '../../../types/Query';
import {v4 as uuidv4} from 'uuid';
import ChipPanel, {ChipPanelHandle} from '../query-panel/chip-panel/ChipPanel';
import ArrOperatorButton from '../operator-button/OperatorButton';
import {getOperatorLabel} from '../operatorMenuUtils';
import FieldTypeDrawer from '../field-type-drawer/FieldTypeDrawer';
import AnalyticsListField from '../field-types/analytics-field-type/AnalyticsFieldType';
import {Field} from '../../../types/Field';

export interface AnalyticsProps {
  query: Query;
  onCriteriaChanged?: (query: Query) => void;
}

class TemporaryCriteria implements Criteria {
  id = uuidv4();
  type: CriteriaType = 'Operand';
}

const Analytics: React.FC<AnalyticsProps> = ({query, onCriteriaChanged}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [tempCriteria, setTempCriteria] = useState<Criteria>(
    new TemporaryCriteria()
  );
  const [insertEnabled, setInsertEnabled] = useState<boolean>(false);
  const chipPanelRef = useRef<ChipPanelHandle>(null);
  const handleCriteriaChanged = (updatedCriteria: Criteria[]) => {
    onCriteriaChanged &&
      onCriteriaChanged({...query, value: [...updatedCriteria]});
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleAnalyticsClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setIsDrawerOpen(true);
  };

  const handleAnalyticsKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>
  ) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      console.error('handleAnalyticsKeyDown > to be implemented');
    }
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const handleDrawerCriteriaChange = (
    field: Field,
    operator: string,
    value: string
  ) => {
    if (value) {
      tempCriteria.value = `${field.text}: ${operator} [ ${value} ]`;
    } else {
      tempCriteria.value = `${field.text}: ${operator}`;
    }
    setTempCriteria(tempCriteria);
  };

  const handleDrawerSave = () => {
    tempCriteria.id = uuidv4();
    chipPanelRef.current?.insertCriteria({...tempCriteria});
    setIsDrawerOpen(false);
  };

  const onInputValid = (valid: boolean) => {
    setInsertEnabled(valid);
  };

  const handleOperatorSelected = (id: string) => {
    const newCriteria: Criteria = {
      id: uuidv4(),
      type: 'Operator',
      value: getOperatorLabel(id),
    };

    chipPanelRef.current?.insertCriteria(newCriteria);
  };
  return (
    <>
      <Stack spacing={1} className="analytics-content-panel">
        <Stack direction={'row'} className="query-actions">
          <Typography
            className="typo-text label3_size query-actions-label"
            data-testid={`analytics-insert`}
          >
            {t('sessionSearch.queryList.analytics.insert')}
          </Typography>
          <ArrButton
            size="small"
            variant="secondary"
            onClick={handleAnalyticsClick}
            onKeyDown={handleAnalyticsKeyDown}
            className="button-als-add-analytics"
            dataTestId="button-als-add-analytics"
          >
            {t('sessionSearch.queryList.analytics.analytics')}
          </ArrButton>
          <ArrOperatorButton
            onOperatorSelected={handleOperatorSelected}
            dataTestId="analytics"
            restoreFocusOnSelect={false}
          />
        </Stack>
        <Box className="query-chips" data-testid="chip-panel-criteria-wrapper">
          <ChipPanel
            ref={chipPanelRef}
            criteria={query.value}
            onCriteriaChange={handleCriteriaChanged}
            dataTestId="analytics-panel"
          />
        </Box>
      </Stack>
      {isDrawerOpen && (
        <FieldTypeDrawer
          title={t('sessionSearch.queryList.analytics.title')}
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          onSave={handleDrawerSave}
          canSave={insertEnabled}
          disableRestoreFocus
        >
          <AnalyticsListField
            onCriteriaChaged={handleDrawerCriteriaChange}
            onInputValid={onInputValid}
          />
        </FieldTypeDrawer>
      )}
    </>
  );
};

export default Analytics;
